// Calendar dates use UTC arithmetic; order time always uses America/New_York.
// Federal holiday rules: https://www.opm.gov/policy-data-oversight/pay-leave/federal-holidays/
const DAY = 86400000;
const holidayCache = new Map();
const key = (date) => date.toISOString().slice(0, 10);
const date = (year, month, day) => new Date(Date.UTC(year, month, day));

function holidays(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const days = new Set();
  const observed = (value) => {
    const weekday = value.getUTCDay();
    days.add(key(new Date(+value + (weekday === 6 ? -DAY : weekday === 0 ? DAY : 0))));
  };
  // Include next New Year's Day: its observed date can be December 31.
  for (const y of [year, year + 1]) {
    for (const [month, day] of [[0, 1], [5, 19], [6, 4], [10, 11], [11, 25]]) {
      observed(date(y, month, day));
    }
  }
  const nth = (month, weekday, occurrence) =>
    date(year, month, 1 + (weekday - date(year, month, 1).getUTCDay() + 7) % 7 + 7 * (occurrence - 1));
  for (const value of [nth(0, 1, 3), nth(1, 1, 3), nth(8, 1, 1), nth(9, 1, 2), nth(10, 4, 4)]) {
    days.add(key(value));
  }
  const lastMay = date(year, 5, 0);
  days.add(key(new Date(+lastMay - ((lastMay.getUTCDay() + 6) % 7) * DAY)));
  holidayCache.set(year, days);
  return days;
}

export function isBusinessDay(value) {
  return value.getUTCDay() !== 0 && value.getUTCDay() !== 6 && !holidays(value.getUTCFullYear()).has(key(value));
}

function addBusinessDays(value, count) {
  let result = new Date(+value);
  while (count > 0) {
    result = new Date(+result + DAY);
    if (isBusinessDay(result)) count -= 1;
  }
  return result;
}

export function estimateDelivery(now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', hourCycle: 'h23',
  }).formatToParts(now).map(({ type, value }) => [type, value]));
  const ordered = date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
  let processing = new Date(+ordered);
  if (Number(parts.hour) >= 17) processing = new Date(+processing + DAY);
  while (!isBusinessDay(processing)) processing = new Date(+processing + DAY);
  const shipped = [addBusinessDays(processing, 1), addBusinessDays(processing, 3)];
  const delivered = [addBusinessDays(shipped[0], 6), addBusinessDays(shipped[1], 9)];
  return { ordered, processing, shipped, delivered };
}

export function formatDeliveryDate(value, weekday = true, year = false) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', ...(weekday ? { weekday: 'short' } : {}),
    month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}),
  }).format(value);
}

if (typeof customElements !== 'undefined' && !customElements.get('ma-delivery-estimate')) {
  customElements.define('ma-delivery-estimate', class extends HTMLElement {
    connectedCallback() {
      this.update = () => {
        const { ordered, shipped, delivered } = estimateDelivery();
        const crossesYear = delivered[0].getUTCFullYear() !== delivered[1].getUTCFullYear();
        const set = (selector, text) => {
          const element = this.querySelector(selector);
          if (element) element.textContent = text;
        };
        set('[data-delivery-range]', delivered.map((d) => formatDeliveryDate(d, true, crossesYear)).join(' - '));
        set('[data-delivery-ordered]', formatDeliveryDate(ordered, false));
        set('[data-delivery-shipped]', shipped.map((d) => formatDeliveryDate(d, false)).join(' - '));
        set('[data-delivery-arrival]', delivered.map((d) => formatDeliveryDate(d, false)).join(' - '));
      };
      this.update();
      this.timer = setInterval(this.update, 60000);
      window.addEventListener('pageshow', this.update);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      window.removeEventListener('pageshow', this.update);
    }
  });
}
